import { defineStore } from 'pinia';

import { getSnackbarActions } from '@evidentid/dashboard-commons/modules/snackbar';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { ChatMessageErrorType } from '@evidentid/tprm-portal-lib/models/evie/ChatMessageErrorType.model';
import { ChatMessageRole } from '@evidentid/tprm-portal-lib/models/evie/ChatMessageRole.model';
import { EvieConversationSummary } from '@evidentid/tprm-portal-lib/models/evie/EvieConversationSummary.model';
import { SseEvent } from '@evidentid/tprm-portal-lib/models/evie/SseEvent.model';

import { EvieConversation } from './models/EvieConversation.model';
import { EvieThreadMessage } from './models/EvieThreadMessage.model';

export const OPTIMISTIC_CONVERSATION_ID = 'optimistic' as const;
const MAX_CONVERSATION_CACHE_SIZE = 20;

// Holds AbortControllers for in-progress streaming requests, keyed by conversationId.
// Module-level (not in Vuex state) because AbortController is not serializable.
const streamControllers: Record<string, AbortController> = {};

function abortStream(conversationId: string): void {
    streamControllers[conversationId]?.abort();
    delete streamControllers[conversationId];
}

function abortAllStreams(): void {
    Object.keys(streamControllers).forEach(abortStream);
}

export interface EvieAiRequirement {
    gatewayClient: GatewayApiClient;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): EvieAiRequirement {
    return store.$app;
}

interface EvieAiState {
    activeConversation: EvieConversation | null;
    activeConversationId: string | null;
    conversationSummaries: Record<string, EvieConversationSummary>;
    conversationCache: Record<string, EvieConversation>;
    sendStatus: 'idle' | 'loading';
    streamingMessages: Record<string, string>;
    loadingConversation: boolean;
    loadingList: boolean;
}

function generateFeId(): string {
    return `fe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createEmptyConversation(conversationId: string): EvieConversation {
    return { conversationId, messagesById: {}, messageIds: [], inputDraft: '' };
}

function buildErrorMessage(errorType: ChatMessageErrorType): EvieThreadMessage {
    return {
        feId: generateFeId(),
        role: ChatMessageRole.AGENT,
        content: { text: '' },
        errorType,
        createdAt: Date.now(),
    };
}

export const useEvieAiStore = defineStore('evieAi', {
    state: (): EvieAiState => ({
        activeConversation: null,
        activeConversationId: null,
        conversationSummaries: {},
        conversationCache: {},
        sendStatus: 'idle',
        streamingMessages: {},
        loadingConversation: false,
        loadingList: false,
    }),
    actions: {
        createConversation(payload: { conversationId: string, summary: EvieConversationSummary }) {
            this.activeConversationId = payload.conversationId;
            this.activeConversation = createEmptyConversation(payload.conversationId);
            this.conversationSummaries = { ...this.conversationSummaries, [payload.conversationId]: payload.summary };
        },
        restoreConversation(conversation: EvieConversation) {
            this.activeConversation = conversation;
            this.activeConversationId = conversation.conversationId;
            // activeConversation and conversationCache[id] intentionally share the same object
            // reference so that background stream mutations (whole-object replacement of
            // messagesById on that shared object) propagate to activeConversation for free —
            // do not replace with an independent copy.
            const updated = { ...this.conversationCache, [conversation.conversationId]: conversation };
            const keys = Object.keys(updated);
            if (keys.length > MAX_CONVERSATION_CACHE_SIZE) {
                for (const key of keys.slice(0, keys.length - MAX_CONVERSATION_CACHE_SIZE)) {
                    delete updated[key];
                }
            }
            this.conversationCache = updated;
        },
        restoreConversationList(summaries: EvieConversationSummary[]) {
            this.conversationSummaries = summaries.reduce(
                (acc, s) => ({ ...acc, [s.conversationId]: s }),
                {} as Record<string, EvieConversationSummary>,
            );
        },
        renameConversationTitle(payload: { conversationId: string, title: string }) {
            const s = this.conversationSummaries[payload.conversationId];
            if (s) {
                this.conversationSummaries = {
                    ...this.conversationSummaries,
                    [payload.conversationId]: { ...s, title: payload.title },
                };
            }
        },
        startLoadingConversation() {
            this.loadingConversation = true;
        },
        finishLoadingConversation() {
            this.loadingConversation = false;
        },
        failLoadingConversation() {
            this.loadingConversation = false;
        },
        startLoadingList() {
            this.loadingList = true;
        },
        finishLoadingList() {
            this.loadingList = false;
        },
        appendMessage(msg: EvieThreadMessage) {
            if (!this.activeConversation) {
                return;
            }
            this.activeConversation.messagesById = { ...this.activeConversation.messagesById, [msg.feId]: msg };
            this.activeConversation.messageIds.push(msg.feId);
        },
        removeMessage(feId: string) {
            if (!this.activeConversation) {
                return;
            }
            this.activeConversation.messageIds = this.activeConversation.messageIds.filter(
                (id) => id !== feId,
            );
            const { [feId]: _removed, ...remainingMessages } = this.activeConversation.messagesById;
            this.activeConversation.messagesById = remainingMessages;
        },
        clearActiveConversation() {
            // Don't abort — stream continues in background (same as switchToConversation).
            this.activeConversation = null;
            this.activeConversationId = null;
        },
        clearAll() {
            abortAllStreams();
            this.activeConversation = null;
            this.activeConversationId = null;
            this.conversationSummaries = {};
            this.conversationCache = {};
            this.streamingMessages = {};
        },
        startOptimisticConversation(userMsg: EvieThreadMessage) {
            const conversation = createEmptyConversation(OPTIMISTIC_CONVERSATION_ID);
            conversation.messagesById[userMsg.feId] = userMsg;
            conversation.messageIds.push(userMsg.feId);
            this.activeConversation = conversation;
            this.activeConversationId = OPTIMISTIC_CONVERSATION_ID;
            this.sendStatus = 'loading';
        },
        confirmConversation(payload: { conversationId: string, summary: EvieConversationSummary }) {
            if (this.activeConversation) {
                this.activeConversation.conversationId = payload.conversationId;
                const { [OPTIMISTIC_CONVERSATION_ID]: _removed, ...rest } = this.conversationCache;
                this.conversationCache = { ...rest, [payload.conversationId]: this.activeConversation };
            }
            this.activeConversationId = payload.conversationId;
            this.conversationSummaries = { [payload.conversationId]: payload.summary, ...this.conversationSummaries };
            this.sendStatus = 'idle';
        },
        failConversationCreation() {
            const errMsg = buildErrorMessage(ChatMessageErrorType.NETWORK);
            if (this.activeConversation) {
                this.activeConversation.messagesById = {
                    ...this.activeConversation.messagesById,
                    [errMsg.feId]: errMsg,
                };
                this.activeConversation.messageIds.push(errMsg.feId);
            }
            // Reset to null so retry re-enters the optimistic path in sendMessage.
            // activeConversation is kept so the error message stays visible.
            this.activeConversationId = null;
            this.sendStatus = 'idle';
        },
        startStreamingMessage(conversationId: string) {
            this.streamingMessages = { ...this.streamingMessages, [conversationId]: '' };
        },
        appendStreamingDelta(payload: { conversationId: string, delta: string }) {
            if (payload.conversationId in this.streamingMessages) {
                this.streamingMessages[payload.conversationId] += payload.delta;
            }
        },
        commitStreamingMessage(payload: { conversationId: string, createdAt: number, runId: string | null }) {
            const target = this.conversationCache[payload.conversationId];
            const text = this.streamingMessages[payload.conversationId];
            // Use key-existence check (not truthiness) so an empty-string response still
            // produces an agent message; a falsy guard would silently drop a `done` event
            // that arrived before any `token` events.
            if (target && payload.conversationId in this.streamingMessages) {
                const agentMsg: EvieThreadMessage = {
                    feId: generateFeId(),
                    role: ChatMessageRole.AGENT,
                    content: { text },
                    createdAt: payload.createdAt,
                    runId: payload.runId,
                };
                target.messagesById = { ...target.messagesById, [agentMsg.feId]: agentMsg };
                target.messageIds.push(agentMsg.feId);
            }
            const { [payload.conversationId]: _done, ...remainingStreams } = this.streamingMessages;
            this.streamingMessages = remainingStreams;
        },
        setMessageFeedback(payload: { conversationId: string, feId: string, score: 'UP' | 'DOWN' | null }) {
            const conversation = this.conversationCache[payload.conversationId];
            if (conversation?.messagesById[payload.feId]) {
                conversation.messagesById = {
                    ...conversation.messagesById,
                    [payload.feId]: { ...conversation.messagesById[payload.feId], feedbackScore: payload.score },
                };
            }
        },
        failStreamingMessage(conversationId: string) {
            const target = this.conversationCache[conversationId];
            const errMsg = buildErrorMessage(ChatMessageErrorType.NETWORK);
            if (target) {
                target.messagesById = { ...target.messagesById, [errMsg.feId]: errMsg };
                target.messageIds.push(errMsg.feId);
            }
            const { [conversationId]: _failed, ...streamsAfterFail } = this.streamingMessages;
            this.streamingMessages = streamsAfterFail;
        },

        async loadConversationList(payload: { rpName: string }) {
            this.startLoadingList();
            try {
                const response = await appOf(this).gatewayClient.tprmUi().getEvieConversations(payload.rpName);
                this.restoreConversationList(response.conversations);
            } catch (error) {
                console.error('[evieAi] Failed to load conversation list:', error);
            } finally {
                this.finishLoadingList();
            }
        },
        async loadConversation(payload: { rpName: string, conversationId: string }) {
            this.startLoadingConversation();
            try {
                const response = await appOf(this).gatewayClient.tprmUi()
                    .getEvieConversation(payload.rpName, payload.conversationId);
                const conversation: EvieConversation = {
                    conversationId: payload.conversationId,
                    messagesById: {},
                    messageIds: [],
                    inputDraft: '',
                };
                for (const msg of response.messages) {
                    const feId = generateFeId();
                    const threadMsg: EvieThreadMessage = { feId, ...msg };
                    conversation.messagesById[feId] = threadMsg;
                    conversation.messageIds.push(feId);
                }
                this.restoreConversation(conversation);
            } catch {
                this.clearActiveConversation();
            } finally {
                this.finishLoadingConversation();
            }
        },
        async sendMessage(payload: { rpName: string, text: string }) {
            if (this.activeConversationId === OPTIMISTIC_CONVERSATION_ID) {
                return;
            }

            const userMsg: EvieThreadMessage = {
                feId: generateFeId(),
                role: ChatMessageRole.USER,
                content: { text: payload.text },
                createdAt: Date.now(),
            };

            if (this.activeConversationId === null) {
                this.startOptimisticConversation(userMsg);
                try {
                    const { conversationId } = await appOf(this).gatewayClient.tprmUi()
                        .createEvieConversation(payload.rpName);
                    this.confirmConversation({
                        conversationId,
                        summary: { conversationId, title: payload.text.slice(0, 80) },
                    });
                } catch {
                    this.failConversationCreation();
                    return;
                }
            } else {
                this.appendMessage(userMsg);
            }

            const conversationId = this.activeConversationId!;

            // Only abort a previous stream if it belongs to THIS conversation.
            // Background streams for other conversations continue uninterrupted.
            if (conversationId in this.streamingMessages) {
                abortStream(conversationId);
            }
            streamControllers[conversationId] = new AbortController();
            const signal = streamControllers[conversationId].signal;

            this.startStreamingMessage(conversationId);

            try {
                await appOf(this).gatewayClient.tprmUi().sendEvieMessageStreaming(
                    payload.rpName,
                    conversationId,
                    payload.text,
                    {
                        signal,
                        onEvent: (sseEvent: SseEvent) => {
                            if (sseEvent.type === 'token') {
                                this.appendStreamingDelta({
                                    conversationId,
                                    delta: sseEvent.data.text,
                                });
                            } else if (sseEvent.type === 'done') {
                                this.commitStreamingMessage({
                                    conversationId,
                                    createdAt: sseEvent.data.createdAt,
                                    runId: sseEvent.data.runId,
                                });
                            } else if (sseEvent.type === 'error') {
                                this.failStreamingMessage(conversationId);
                            }
                            // toolStart / toolEnd / toolError: ignored in v1
                        },
                    },
                );
            } catch {
                // Network failure, inactivity timeout, or caller-initiated abort.
                if (conversationId in this.streamingMessages) {
                    this.failStreamingMessage(conversationId);
                }
            } finally {
                if (streamControllers[conversationId]?.signal === signal) {
                    delete streamControllers[conversationId];
                }
            }
        },
        async retryMessage(payload: { rpName: string, feId: string }) {
            const conversation = this.activeConversation;
            if (!conversation) {
                return;
            }
            const errIdx = conversation.messageIds.indexOf(payload.feId);
            if (errIdx < 1) {
                return;
            }
            const userFeId = conversation.messageIds[errIdx - 1];
            const userMsg = conversation.messagesById[userFeId];
            if (!userMsg || userMsg.role !== ChatMessageRole.USER) {
                return;
            }
            this.removeMessage(payload.feId);
            await this.sendMessage({ rpName: payload.rpName, text: userMsg.content.text });
        },
        async switchToConversation(payload: { rpName: string, conversationId: string }) {
            // Don't abort — the stream continues in the background keyed to its own conversationId.
            // sendMessage calls abortStream(conversationId) before starting any new stream for the same
            // conversation, so a new send will cleanly replace it.
            const cached = this.conversationCache[payload.conversationId];
            if (cached) {
                this.restoreConversation(cached);
            } else {
                await this.loadConversation(payload);
            }
        },
        applyConversationTitleUpdate(payload: { conversationId: string, title: string }) {
            this.renameConversationTitle(payload);
        },
        setInputDraft(payload: { draft: string }) {
            if (this.activeConversation) {
                this.activeConversation.inputDraft = payload.draft;
            }
        },
        cancelStream(_payload: { rpName: string }) {
            const conversationId = this.activeConversationId;
            if (!conversationId || !(conversationId in this.streamingMessages)) {
                return;
            }
            this.commitStreamingMessage({ conversationId, createdAt: Date.now(), runId: null });
            // TODO(PRODUCT-25807): uncomment once chatbot-broker stop-conversation endpoint ships:
            // appOf(this).gatewayClient.tprmUi().stopEvieConversation(_payload.rpName, conversationId);
            abortStream(conversationId);
        },
        async submitFeedback(payload: { rpName: string, conversationId: string, feId: string, score: 'UP' | 'DOWN' }) {
            const message = this.conversationCache[payload.conversationId]?.messagesById[payload.feId];
            if (!message?.runId || message.feedbackScore === payload.score) {
                return;
            }
            const previousScore = message.feedbackScore ?? null;
            this.setMessageFeedback(
                { conversationId: payload.conversationId, feId: payload.feId, score: payload.score },
            );
            try {
                await appOf(this).gatewayClient.tprmUi().submitEvieFeedback(payload.rpName, payload.conversationId, {
                    runId: message.runId,
                    score: payload.score,
                });
            } catch {
                this.setMessageFeedback(
                    { conversationId: payload.conversationId, feId: payload.feId, score: previousScore },
                );
                await getSnackbarActions().displaySnackbar({
                    success: false,
                    message: 'Failed to submit feedback. Please try again.',
                });
            }
        },
    },
});
